import { type ReactElement, useState, useRef } from 'react';
import { Trash2, Plus, Upload, ExternalLink, Music, FileText } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { YouTubeEmbed } from '@/components/youtube-embed';
import { AuthedAudio, AuthedDownloadLink } from '@/components/molecules/authed-media';
import { isAudioMimeType } from '@/lib/resource-media';
import type { ArrangementResource } from '@/lib/types';

type AddLinkData = {
  resourceType: string;
  sourceType: string;
  title: string;
  url: string;
};

export type UploadStatus = {
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  errorMessage?: string;
};

type Props = {
  resources: ArrangementResource[];
  onAddLink: (data: AddLinkData) => void;
  onUploadFile: (file: File, resourceType: string, title: string) => void;
  onDelete: (resourceId: string) => void;
  uploadStatus?: UploadStatus;
};

const RESOURCE_TYPE_OPTIONS = [
  { value: 'recording', label: 'Recording' },
  { value: 'sheet_music', label: 'Sheet Music' },
  { value: 'link', label: 'Link' },
];

const SOURCE_TYPE_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'url', label: 'URL' },
];

function detectResourceType(mimeType: string): string {
  if (isAudioMimeType(mimeType)) return 'recording';
  if (mimeType === 'application/pdf') return 'sheet_music';
  return 'link';
}

function ResourceCard({ resource, onDelete }: { resource: ArrangementResource; onDelete: () => void }): ReactElement {
  return (
    <div className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {isAudioMimeType(resource.mimeType) && <Music className="h-4 w-4 text-[var(--ink-3)]" />}
          {resource.mimeType === 'application/pdf' && <FileText className="h-4 w-4 text-[var(--ink-3)]" />}
          {resource.sourceType === 'youtube' && <ExternalLink className="h-4 w-4 text-[var(--ink-3)]" />}
          {resource.sourceType === 'spotify' && <Music className="h-4 w-4 text-[var(--ink-3)]" />}
          <span className="text-sm font-medium text-[var(--ink)]">{resource.title}</span>
        </div>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete"
          className="shrink-0 rounded-lg p-1 text-[var(--ink-3)] transition-colors hover:bg-[color:var(--err)]/10 hover:text-[var(--err)]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {resource.sourceType === 'youtube' && resource.url && (
        <YouTubeEmbed url={resource.url} />
      )}

      {resource.sourceType === 'spotify' && resource.url && (
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-[var(--brand)] hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Open in Spotify
        </a>
      )}

      {isAudioMimeType(resource.mimeType) && resource.downloadUrl && (
        <AuthedAudio downloadUrl={resource.downloadUrl} />
      )}

      {resource.mimeType === 'application/pdf' && resource.downloadUrl && (
        <AuthedDownloadLink downloadUrl={resource.downloadUrl} label={resource.title} />
      )}

      {resource.sourceType === 'url' && resource.url && (
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-[var(--brand)] hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {resource.url}
        </a>
      )}
    </div>
  );
}

function AddLinkForm({ onSubmit, onCancel }: { onSubmit: (data: AddLinkData) => void; onCancel: () => void }): ReactElement {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [resourceType, setResourceType] = useState('recording');
  const [sourceType, setSourceType] = useState('youtube');

  function handleSubmit(): void {
    if (!title.trim() || !url.trim()) return;
    onSubmit({ title, url, resourceType, sourceType });
  }

  const inputClass =
    'h-10 w-full rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-sm text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20';

  const selectClass =
    'h-10 flex-1 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none';

  return (
    <div className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] p-3 space-y-2">
      <input type="text" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
      <input type="url" placeholder="URL" value={url} onChange={(e) => setUrl(e.target.value)} className={inputClass} />
      <div className="flex gap-2">
        <select value={resourceType} onChange={(e) => setResourceType(e.target.value)} className={selectClass}>
          {RESOURCE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} className={selectClass}>
          {SOURCE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={!title.trim() || !url.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}

export function ResourceSection({ resources, onAddLink, onUploadFile, onDelete, uploadStatus }: Props): ReactElement {
  const [showAddLink, setShowAddLink] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPending = uploadStatus?.isPending ?? false;
  const isError = uploadStatus?.isError ?? false;
  const isSuccess = uploadStatus?.isSuccess ?? false;
  const errorMessage = uploadStatus?.errorMessage ?? 'Upload failed.';

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    const resourceType = detectResourceType(file.type);
    const title = file.name.replace(/\.[^/.]+$/, '');
    onUploadFile(file, resourceType, title);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => setShowAddLink(true)}>
          <Plus className="h-3 w-3" />
          Add link
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-3 w-3" />
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.m4a,.wav,.ogg,.oga,.aac,.flac,.opus,.weba,audio/*,.pdf,application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {isPending && (
        <p className="text-sm text-[var(--ink-3)]" aria-live="polite">
          Uploading…
        </p>
      )}
      {isError && (
        <div
          role="alert"
          className="rounded-[var(--r-sm)] border border-[color:var(--err)]/30 bg-[color:var(--err)]/10 px-3 py-2 text-sm text-[var(--err)]"
        >
          {errorMessage}
        </div>
      )}
      {isSuccess && !isPending && (
        <p
          className="text-sm text-[var(--ok)]"
          aria-live="polite"
        >
          Uploaded.
        </p>
      )}

      {showAddLink && (
        <AddLinkForm
          onSubmit={(data) => {
            onAddLink(data);
            setShowAddLink(false);
          }}
          onCancel={() => setShowAddLink(false)}
        />
      )}

      {resources.length === 0 ? (
        <p className="text-sm text-[var(--ink-3)]">No resources yet.</p>
      ) : (
        <div className="space-y-2">
          {resources.map((resource) => (
            <ResourceCard
              key={resource.arrangementResourceId}
              resource={resource}
              onDelete={() => onDelete(resource.arrangementResourceId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
